/**
 * Smart Tours — landlord-side console for prerecorded walkthrough URLs
 * and live/self-tour booking requests. Pairs with the
 * smart_tours_service backend.
 *
 * NOTE: This sits alongside the existing /tours page (which talks to
 * the external core backend for video uploads + Smart marketing copy).
 * This page is purely for the NEW local-backed self-tour + live tour
 * workflow.
 */
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Textarea } from "@/components/ui/Primitives";
import { Video, Play, Plus, Calendar, CheckCircle2, X, Key as KeyIcon, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";
import MatterportAttachPanel from "@/components/MatterportAttachPanel";
import VirtualStagingModal from "@/components/VirtualStagingModal";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function SmartTours() {
  const { user } = useAuth();
  const uid = user?.id || user?.user_id || user?.email || "";
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoOpen, setVideoOpen] = useState(null); // listing
  const [confirmOpen, setConfirmOpen] = useState(null); // booking
  const [vform, setVform] = useState({ video_url: "", title: "", cover_image_url: "" });
  const [cform, setCform] = useState({ confirmed_slot: "", meeting_link: "", note: "", access_code_id: "" });
  const [busy, setBusy] = useState(false);
  const [stagingOpen, setStagingOpen] = useState(false);

  const load = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/api/listings?limit=100`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid } }).then(r => r.json()),
        fetch(`${API}/api/tours/bookings`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid } }).then(r => r.json()),
      ]);
      setListings((r1.items || []).filter(l => l.landlord_id === uid));
      setBookings(r2.items || []);
    } finally { setLoading(false); }
  }, [uid]);
  useEffect(() => { load(); }, [load]);

  async function saveVideo(e) {
    e.preventDefault();
    if (!videoOpen) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/tours/listing/${videoOpen.id}/video`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid },
        body: JSON.stringify(vform),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not save");
      toast.success("Tour video saved");
      setVideoOpen(null);
      setVform({ video_url: "", title: "", cover_image_url: "" });
      load();
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  async function removeVideo(listing) {
    if (!window.confirm("Remove the tour video from this listing?")) return;
    await fetch(`${API}/api/tours/listing/${listing.id}/video`, { method: "DELETE", headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid } });
    load();
  }

  async function confirmBooking(e) {
    e.preventDefault();
    if (!confirmOpen) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/tours/bookings/${confirmOpen.id}/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid },
        body: JSON.stringify(cform),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not confirm");
      toast.success("Booking confirmed");
      setConfirmOpen(null);
      setCform({ confirmed_slot: "", meeting_link: "", note: "", access_code_id: "" });
      load();
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  async function cancelBooking(bk) {
    if (!window.confirm("Cancel this tour request?")) return;
    await fetch(`${API}/api/tours/bookings/${bk.id}/cancel`, { method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid } });
    load();
  }

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Smart tours"
        subtitle="Attach video walkthroughs to your listings and manage live + self-tour bookings."
      />

      <div className="mb-3 flex justify-end">
        <Button onClick={() => setStagingOpen(true)} variant="secondary" data-testid="virtual-staging-launch">
          <Sparkles className="w-4 h-4 inline mr-1"/> Virtual staging (render 3 layouts)
        </Button>
      </div>

      <MatterportAttachPanel listings={listings} uid={uid} onRefresh={load} />

      <h3 className="font-display text-lg font-bold text-slate-900 mb-3 mt-2 flex items-center gap-2"><Video className="w-4 h-4 text-emerald-700"/> Walkthrough videos</h3>
      {listings.length === 0 ? (
        <EmptyState icon={Video} title="No listings yet" description="Publish a listing first, then attach a video walkthrough so prospective tenants can self-tour without you present."/>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          {listings.map(l => (
            <Card key={l.id} className="p-4" data-testid={`tour-listing-${l.id}`}>
              <div className="font-bold text-slate-900 truncate">{l.title}</div>
              <div className="text-xs text-slate-500 mt-0.5 mb-3">{l.city}, {l.province} · ${l.rent_cad}/mo</div>
              {l.tour_video_url ? (
                <div className="space-y-2">
                  <Pill tone="green"><Play className="w-3 h-3 inline mr-1"/> Tour active</Pill>
                  <div className="flex gap-2">
                    <a href={l.tour_video_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-emerald-700 inline-flex items-center gap-1 hover:underline" data-testid={`tour-preview-${l.id}`}><ExternalLink className="w-3 h-3"/> Preview</a>
                    <button onClick={() => { setVform({ video_url: l.tour_video_url || "", title: l.tour_title || "", cover_image_url: l.tour_cover_image_url || "" }); setVideoOpen(l); }} className="text-xs font-bold text-slate-600 hover:text-slate-900" data-testid={`tour-edit-${l.id}`}>Edit</button>
                    <button onClick={() => removeVideo(l)} className="text-xs font-bold text-rose-600 hover:text-rose-700" data-testid={`tour-remove-${l.id}`}>Remove</button>
                  </div>
                </div>
              ) : (
                <Button onClick={() => { setVform({ video_url: "", title: "", cover_image_url: "" }); setVideoOpen(l); }} data-testid={`tour-add-${l.id}`} className="!py-1.5 !px-3 text-xs">
                  <Plus className="w-3 h-3 inline mr-1"/> Add walkthrough
                </Button>
              )}
            </Card>
          ))}
        </div>
      )}

      <h3 className="font-display text-lg font-bold text-slate-900 mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-emerald-700"/> Booking requests <Pill tone="slate">{bookings.filter(b => b.status === "pending").length} pending</Pill></h3>
      {bookings.length === 0 ? (
        <EmptyState icon={Calendar} title="No tour requests yet" description="When a prospective tenant requests a live or self-tour, it'll appear here for you to confirm."/>
      ) : (
        <div className="space-y-2">
          {bookings.map(bk => (
            <Card key={bk.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`booking-${bk.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-slate-900">{bk.tenant_name}</span>
                  <Pill tone={bk.status === "confirmed" ? "green" : bk.status === "pending" ? "amber" : "slate"}>{bk.status}</Pill>
                  {bk.self_tour && <Pill tone="blue"><KeyIcon className="w-2.5 h-2.5 inline mr-1"/> Self-tour</Pill>}
                </div>
                <div className="text-xs text-slate-600 mt-1">{bk.tenant_email}{bk.tenant_phone ? ` · ${bk.tenant_phone}` : ""}</div>
                {bk.preferred_slots?.length > 0 && <div className="text-xs text-slate-500 mt-0.5">Preferred: {bk.preferred_slots.join(" · ")}</div>}
                {bk.confirmed_slot && <div className="text-xs text-emerald-700 mt-0.5"><CheckCircle2 className="w-3 h-3 inline mr-1"/> Confirmed for {bk.confirmed_slot}</div>}
                {bk.meeting_link && <a href={bk.meeting_link} target="_blank" rel="noreferrer" className="text-xs font-bold text-emerald-700 hover:underline inline-flex items-center gap-1 mt-0.5"><ExternalLink className="w-3 h-3"/> Open meeting link</a>}
                {bk.note && <div className="text-xs text-slate-500 italic mt-1">&ldquo;{bk.note}&rdquo;</div>}
              </div>
              {bk.status === "pending" && (
                <div className="flex gap-2 shrink-0">
                  <Button onClick={() => { setCform({ confirmed_slot: bk.preferred_slots?.[0] || "", meeting_link: "", note: "", access_code_id: "" }); setConfirmOpen(bk); }} className="!py-1.5 !px-3 text-xs" data-testid={`booking-confirm-${bk.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 inline mr-1"/> Confirm
                  </Button>
                  <button onClick={() => cancelBooking(bk)} className="px-3 py-1.5 rounded text-xs font-bold text-rose-600 hover:bg-rose-50" data-testid={`booking-cancel-${bk.id}`}><X className="w-3.5 h-3.5"/></button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Modal open={!!videoOpen} onClose={() => setVideoOpen(null)} title={`Attach walkthrough — ${videoOpen?.title || ""}`} testid="video-modal">
        <form onSubmit={saveVideo} className="space-y-4">
          <Input label="Video URL" required placeholder="https://youtube.com/… or any mp4 link" value={vform.video_url} onChange={(e) => setVform({...vform, video_url: e.target.value})} testid="video-url-input"/>
          <Input label="Cover image URL (optional)" placeholder="https://… (poster shown before play)" value={vform.cover_image_url} onChange={(e) => setVform({...vform, cover_image_url: e.target.value})}/>
          <Input label="Title (optional)" placeholder="Quick tour of the 2-bedroom" value={vform.title} onChange={(e) => setVform({...vform, title: e.target.value})}/>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setVideoOpen(null)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="video-save">{busy ? "Saving…" : "Save"}</Button>
          </div>
        </form>
      </Modal>

      <Modal open={!!confirmOpen} onClose={() => setConfirmOpen(null)} title={`Confirm tour — ${confirmOpen?.tenant_name || ""}`} testid="confirm-modal">        <form onSubmit={confirmBooking} className="space-y-4">
          <Input label="Confirmed slot" required placeholder="Sat Mar 15, 2pm" value={cform.confirmed_slot} onChange={(e) => setCform({...cform, confirmed_slot: e.target.value})} testid="confirm-slot-input"/>
          <Input label="Meeting link (Google Meet / Zoom / Whereby)" placeholder="https://meet.google.com/abc-def-ghi" value={cform.meeting_link} onChange={(e) => setCform({...cform, meeting_link: e.target.value})}/>
          {confirmOpen?.self_tour && (
            <Input label="Smart-lock access code id (optional)" placeholder="cd_…" value={cform.access_code_id} onChange={(e) => setCform({...cform, access_code_id: e.target.value})}/>
          )}
          <Textarea label="Note to tenant (optional)" placeholder="Building entrance is on Bloor St — buzzer 304." value={cform.note} onChange={(e) => setCform({...cform, note: e.target.value})}/>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setConfirmOpen(null)}>Back</Button>
            <Button type="submit" disabled={busy} data-testid="confirm-submit">{busy ? "Confirming…" : "Confirm booking"}</Button>
          </div>
        </form>
      </Modal>

      <VirtualStagingModal open={stagingOpen} onClose={() => setStagingOpen(false)} />
    </div>
  );
}
